import type { FC, ReactElement } from "react"

import { AnimatePresence, LazyMotion, domAnimation, motion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import { ErrorBoundary } from "react-error-boundary"
import {
    type NavigateFunction,
    type Params,
    type SetURLSearchParams,
    useNavigate,
    useParams,
    useSearchParams
} from "react-router-dom"
import { useMount, useShallowCompareEffect } from "react-use"

import { ErrorHint } from "@/3514/components"
import { ECoachTab, EPath } from "@/3514/constants"
import { diagnosticSummaryEntityLoader } from "@/3514/entities/diagnostic-summary"
import {
    EWindowCustomEvent,
    type TScreenBreakpoints,
    type TWindowCustomEventDispatcher,
    useIsNetworkFast,
    useScreenBreakpoints,
    useWindowCustomEventDispatcher
} from "@/3514/hooks"
import { type TParticipantByIdQuery, useParticipantByIdQuery } from "@/3514/queries"
import { idleCb, seconds } from "@/3514/utils"

import classes from "./coach.group-participant.styles"
import DiagnosticTab from "./coach.group-participant.tab.diagnostic"
import ModulesTab from "./coach.group-participant.tab.modules"
import SessionsTab from "./coach.group-participant.tab.sessions"

const CoachGroupParticipantPage: FC = (): ReactElement => {
    const navigate: NavigateFunction = useNavigate()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isNetworkFast: boolean = useIsNetworkFast()

    const [searchParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams()

    const { participantId: participantIdParam, groupId: groupIdParam }: Readonly<Params> = useParams()

    const participantId: number = Number(participantIdParam)

    const tabKey: string = searchParams.get("tab")

    const isModulesTab: boolean = tabKey === ECoachTab.GroupParticipant.Modules,
        isSessionsTab: boolean = tabKey === ECoachTab.GroupParticipant.Sessions,
        isDiagnosticTab: boolean = tabKey === ECoachTab.GroupParticipant.Diagnostic

    useMount((): void => {
        if (isEmpty(participantIdParam) || isEmpty(groupIdParam))
            navigate(EPath.Coach_Dashboard({ tab: "Sessions" }), { replace: true })

        if (!isDiagnosticTab && isNetworkFast) idleCb((): void => void diagnosticSummaryEntityLoader(), seconds(30))
    })

    const {
        data: participant,
        isSuccess: isParticipantQuerySuccess,
        isFetching: isParticipantQueryLoading
    }: TParticipantByIdQuery = useParticipantByIdQuery({
        userId: participantId
    })

    const dispatchWindowCustomEvent: TWindowCustomEventDispatcher<EWindowCustomEvent.CoachGroupParticipantMeta> =
        useWindowCustomEventDispatcher(EWindowCustomEvent.CoachGroupParticipantMeta)

    useShallowCompareEffect((): void => {
        if (!isParticipantQuerySuccess) return

        function dispatcher(): void {
            dispatchWindowCustomEvent({
                firstName: participant.firstName,
                lastName: participant.lastName,
                photo: participant.photo,
                cohortName: participant.cohortName,
                groupName: undefined // TODO: backend add
            })
        }

        if (breakpoints.xs || breakpoints.sm) requestAnimationFrame((): void => dispatcher())
        else idleCb((): void => dispatcher(), seconds(10))
    }, [isParticipantQuerySuccess, dispatchWindowCustomEvent, participant])

    return (
        <ErrorBoundary
            resetKeys={[participantId, groupIdParam, tabKey]}
            fallback={<ErrorHint isVisible containerClassName={classes.page.error()} />}
        >
            <LazyMotion features={domAnimation}>
                <AnimatePresence mode="wait" initial={false}>
                    <motion.div
                        key={tabKey}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.2, ease: "easeInOut" }}
                        style={{ display: "flex", flexDirection: "column" }}
                    >
                        {isModulesTab && <ModulesTab participantId={participantId} />}

                        {isDiagnosticTab && (
                            <DiagnosticTab
                                participantId={participantId}
                                isParticipantLoading={isParticipantQueryLoading}
                                isDiagnosticEnabled={
                                    isParticipantQuerySuccess
                                        ? participant.isDiagnosticPresent || participant.isReflectionPresent
                                        : false
                                }
                            />
                        )}

                        {isSessionsTab && <SessionsTab participantId={participantId} />}
                    </motion.div>
                </AnimatePresence>
            </LazyMotion>
        </ErrorBoundary>
    )
}

CoachGroupParticipantPage.displayName = "CoachGroupParticipantPage"

export { CoachGroupParticipantPage }
